import * as React from 'react';
import { Library, ShareLibraryType } from '@moovly/clients/lib/resources/libraries/libraries.interfaces';
import useCurrentUser from '../../../hooks/useCurrentUser';
import { getLibraryShareTypeOptions } from '../config/libraries';
import { useDispatch } from 'react-redux';
import { t } from '@moovly/i18n';
import { ModalFooter, Select } from '@moovly/ui';
import { createLibrary, editLibrary } from '../store/libraries.store';

interface LibraryCreateUpdateContainerProps {
  library?: Library;
  onClose: () => void;
}

const LibraryCreateUpdateContainer: React.FC<LibraryCreateUpdateContainerProps> = ({ library, onClose }) => {
  const currentUser = useCurrentUser();
  const shareTypeOptions = React.useMemo(() => getLibraryShareTypeOptions(currentUser), [currentUser]);
  const dispatch = useDispatch<any>();

  const [state, setState] = React.useState<{ shareType: ShareLibraryType; name: string; externalIds: string[] }>({
    shareType: library && library.shares?.length ? library.shares[0].type : shareTypeOptions[0].value,
    name: library ? library.name : '',
    externalIds: library && library.shares ? library.shares.map((s) => s.id) : [],
  });

  const handleChangeShareType = React.useCallback(
    ({ value }: any) => {
      setState((st) => {
        const result: any = {
          group: currentUser.groupsWithAdminRight.length && currentUser.groupsWithAdminRight[0].id,
          organization: currentUser.parentOrganizationId,
        };
        return {
          ...st,
          shareType: value,
          externalIds: library && library.shares ? library.shares.map((s) => s.id) : [result[value]],
        };
      });
    },
    [currentUser, library]
  );

  const handleChangeGroups = React.useCallback((groups: { value: string }[]) => {
    setState((st) => {
      return { ...st, externalIds: groups.map((gr) => gr.value) };
    });
  }, []);
  const handleChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setState((st) => {
      return { ...st, [e.target.name]: e.target.value };
    });
  }, []);
  const handleConfirm = React.useCallback(() => {
    if (!!library) {
      dispatch(
        editLibrary({
          library: library,
          ...state,
        })
      );
    } else {
      dispatch(createLibrary(state));
    }
    onClose();
  }, [state, onClose, library, dispatch]);
  const groupsSelect = React.useMemo(() => {
    return currentUser.groupsWithAdminRight.map((gr) => ({ value: gr.id, label: gr.name }));
  }, [currentUser.groupsWithAdminRight]);
  return (
    <>
      <div>
        <form onSubmit={handleConfirm}>
          <div>
            <div className="mb-3">
              <label htmlFor="libraryShareType" className="form-label">
                {t('libraries.table.shareType')}
              </label>
              <Select
                id="libraryShareType"
                value={shareTypeOptions.find((sh) => sh.value === state.shareType)}
                options={shareTypeOptions}
                onChange={handleChangeShareType}
              />
            </div>
            {state.shareType === 'group' && groupsSelect.length > 1 && (
              <div className="mb-3">
                <label htmlFor="selectGroup" className="form-label">
                  {t('libraries.create.selectGroup')}
                </label>
                <Select
                  id="selectGroup"
                  isMulti
                  value={groupsSelect.filter((sh) => state.externalIds?.includes(sh.value))}
                  options={groupsSelect}
                  onChange={handleChangeGroups}
                />
              </div>
            )}
            <div className="mb-3">
              <label htmlFor="libraryName" className="form-label">
                {t('libraries.table.name')}
              </label>
              <input
                type="text"
                className="form-control"
                id="libraryName"
                name="name"
                onChange={handleChange}
                value={state.name}
                autoFocus
              />
            </div>
          </div>
        </form>
      </div>
      <ModalFooter confirmButtonText={t('libraries.create.button')} onConfirm={handleConfirm} onClose={onClose} />
    </>
  );
};

export default LibraryCreateUpdateContainer;
